import { useQuery } from 'hooks/useQuery';
import { KeyboardArrowRight } from '@material-ui/icons';
import AmountSummaryTable from 'components/billing/AmountSummaryTable';
import FinancialAssistanceBody from 'components/billing/FinancialAssistanceBody';
import CallToActionRow from 'components/common/CallToActionRow/CallToActionRow';
import CollapsibleList from 'components/common/CollapsibleList/CollapsibleList';
import CollapsibleListItem from 'components/common/CollapsibleList/CollapsibleListItem';
import Divider from 'components/UI/Divider';
import { CenteredSpinner } from 'components/UI/Spinner/SpinnerContainer';
import Typography from 'components/UI/Typography';
import DefaultText from 'components/UI/Typography/DefaultText';
import isEmpty from 'lodash/isEmpty';
import { Color } from 'modules/styles/colors';
import { formatDate } from 'modules/utils/DateUtils';
import React, { ComponentType, useEffect, useState } from 'react';
import { Dimensions, TouchableOpacity } from 'react-native';
import Config from 'react-native-config';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { NavigationScreenProps } from 'screens/navigation';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import { GET_COLLECTIONS_SUMMARY } from 'services/billing/GetCollectionsSummary';
import { GET_PAYMENTPLAN_SUMMARY } from 'services/billing/GetPaymentPlanSummary';
import { GET_GUARANTOR_SUMMARY } from 'services/billing/GetGuarantorSummary';
import { GET_HSA_DETAILS } from 'services/billing/GetHSADetails';
import { myPlanBenefitsSelector, familyPlanBenefitsSelector } from 'store/cost/selectors';
import {
  GetCollectionsSummary,
  GetCollectionsSummary_guarantor_payments as Transaction,
  GetCollectionsSummary_guarantor_paidEncounters as PaidEncounter
} from 'services/billing/__generated__/GetCollectionsSummary';
import {
  GetGuarantorSummary,
  GetGuarantorSummary_guarantor_encounters as Encounter
} from 'services/billing/__generated__/GetGuarantorSummary';
import { GetHSADetails } from 'services/billing/__generated__/GetHSADetails';
import formatMoney from 'services/formatMoney';
import { isImpersonatingSelector } from 'store/account/selectors';
import { getGlossary } from 'store/glossary/actions';
import { GlossaryItem } from 'store/glossary/reducers';
import { getGlossaries, getItemByTerm } from 'store/glossary/selectors';
import { selectHealthFacetsInsuranceSelector } from 'store/profile/selectors';
import { Insurance } from 'store/profile/types';
import * as snacksActions from 'store/snacks/actions';
import { Snack, SnackType } from 'store/snacks/types';
import { RootState } from 'store/types';
import { dfdDefaultTheme, MuiBox, MuiContainer } from 'theme/material-ui';
import { oc } from 'ts-optchain';
import { EmptyMuiBox, GreyView } from '../styled';
import { getProfileForInsurance } from 'store/profile/actions';
import { GetPaymentPlanSummary } from 'services/billing/__generated__/GetPaymentPlanSummary';
import VisitPayWebView from 'screens/Billing/VisitPayWebView';
import { VISIT_PAY_HOME, VISIT_PAY_PAYMENT_PLAN } from '../constants';
import { Svg } from 'components/UI/Svg';
import { IconSize, FontWeight, FontSize } from 'modules/styles/variables';
import TouchablePanelGroup from 'components/UI/Panel/TouchablePanelGroup';
import { BILLS_TAB } from 'store/billing/constants';
import BillingHomeScreen from './BillingHome';
import { ItemBody } from 'store/billing/types';

const NA = 'N/A';
const DASH = 'Payment Plan';
function formatMoneyOrDash(money: number): string {
  const res = formatMoney(money);

  return res ? `$${res}` : DASH;
}

interface TableData extends ItemBody {
  left: string;
  middle: string;
  right: string;
  id?: string;
}

export interface SpendSummaryBody {
  title: string;
  left: string;
  right?: number | string;
  id: string;
  testId: string;
}

interface Props extends NavigationScreenProps {
  isImpersonating: boolean;
  shInsurance: Insurance;
  createSnack: typeof snacksActions.createSnack;
  glossary: GlossaryItem[];
  myBenefits: Record<string, any>;
  familyBenefits: Record<string, any>;
  selectHealthFacetsInsurance: Record<string, any>;
  currentUrl?: string;
  referringUrl?: string;
}

const iff = (condition: boolean, then: string, otherwise: string) => (condition ? then : otherwise);

const renderSpinner = () => <CenteredSpinner opacity={0.5} backgroundColor="#fff" />;

export const BillingHome = ({
  createSnack,
  isImpersonating,
  shInsurance,
  glossary,
  myBenefits,
  familyBenefits,
  selectHealthFacetsInsurance,
  currentUrl,
  referringUrl
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const skip = isImpersonating;
  const data: AmplitudeEventData = {
    currentUrl,
    referringUrl
  };

  const [goToVisitPay, setGoToVisitPay] = React.useState(false);
  const [visitPayURL, setVisitPayURL] = React.useState('');
  const [definitions, setdefinitions] = useState([] as GlossaryItem[]);
  const [spendingSummaryData, setSpendingSummaryData] = useState([] as SpendSummaryBody[]);
  const isExpanded = location.state?.isExpanded === BILLS_TAB;
  const subscriberId = oc(shInsurance).subscriberId('');

  const { data: hsaData, loading: hsaLoading, error: hsaError } = useQuery<GetHSADetails>(
    GET_HSA_DETAILS,
    {
      skip,
      variables: {
        subscriberId,
        hasFacets: !!subscriberId
      },
      errorPolicy: 'all'
    }
  );

  const {
    data: guarantorData,
    loading: guarantorLoading,
    error: guarantorError,
    refetch: guarantorRefetch
  } = useQuery<GetGuarantorSummary>(GET_GUARANTOR_SUMMARY, { skip, errorPolicy: 'all' });

  const { data: paymentsSummary, loading: collectionLoading, error: collectionError } = useQuery<
    GetCollectionsSummary
  >(GET_COLLECTIONS_SUMMARY, { skip });

  const {
    data: paymentPlansData,
    loading: paymentPlansLoading,
    error: paymentPlansError,
    refetch: paymentPlansRefetch
  } = useQuery<GetPaymentPlanSummary>(GET_PAYMENTPLAN_SUMMARY, { skip, errorPolicy: 'all' });

  const hasError = hsaError || collectionError;

  const onError = () => {
    if (hasError && currentUrl === 'Billing') {
      createSnack(SnackType.BASE, {
        label: 'Unable to load data, please try again'
      });
    }
  };

  const refreshBilling = () => {
    // update new data from graphql
    if (guarantorRefetch && paymentPlansRefetch && currentUrl === 'Billing') {
      guarantorRefetch();
      paymentPlansRefetch();
    }
  };

  useEffect(onError, [currentUrl, hasError]);

  useEffect(() => {
    if (!glossary || !glossary.length) {
      dispatch(getGlossary());
    } else {
      const def: GlossaryItem[] = [
        getItemByTerm(glossary, 'Balance Billing'),
        getItemByTerm(glossary, 'Billing Statement'),
        getItemByTerm(glossary, 'Financial Assistance'),
        getItemByTerm(glossary, 'Total Charges')
      ];
      setdefinitions(def);
    }
  }, [glossary]);

  useEffect(() => {
    dispatch(getProfileForInsurance());
  }, []);

  useEffect(() => {
    if (myBenefits) {
      if (
        myBenefits.max_oop &&
        !spendingSummaryData.map(a => a.title).includes('SelectHealth Med Out of Pocket Max')
      ) {
        setSpendingSummaryData(oldArray => [
          {
            id: 'SelectHealth',
            title: 'SelectHealth Med Out of Pocket Max',
            left: 'Individual Out of Pocket Max',
            right: 'Family Out of Pocket Max',
            testId: 'select-health-list-item'
          },
          ...oldArray
        ]);
      }
      if (
        myBenefits.deductible &&
        !spendingSummaryData.map(a => a.title).includes('SelectHealth Med')
      ) {
        setSpendingSummaryData(oldArray => [
          {
            id: 'SelectHealth',
            title: 'SelectHealth Med',
            left: 'Individual Deductible',
            right: 'Family Deductible',
            testId: 'select-health-out-of-pocket-list-item'
          },
          ...oldArray
        ]);
      }
    }
  }, [myBenefits]);

  useEffect(() => {
    if (
      oc(hsaData).hsaDetails.investmentAmount() !== undefined &&
      !spendingSummaryData.map(a => a.title).includes('Health Saving Account')
    ) {
      setSpendingSummaryData(oldArray => [
        ...oldArray,
        {
          id: 'HSA',
          title: 'Health Saving Account',
          left: 'Available Balance',
          testId: 'health-saving-list-item'
        }
      ]);
    }
  }, [hsaData]);

  useEffect(refreshBilling, [currentUrl]);

  const encounters = oc(guarantorData).guarantor.encounters([]);
  const paidEncounters = oc(paymentsSummary).guarantor.paidEncounters([]);
  const payments = oc(paymentsSummary).guarantor.payments([]);
  const collections = oc(paymentsSummary).guarantor.encounters([]);
  const paymentPlans = oc(paymentPlansData).guarantor.paymentPlans([]);
  const checkPaymentPlansEligiblity = oc(paymentPlansData).guarantor.checkPaymentPlansEligiblity();
  const totalEncounters = oc(guarantorData).guarantor.totalEncounters(0);
  const amountDue = oc(guarantorData).guarantor.amountDue(0);
  const isVPeligible = encounters.every(
    (eachEncounter: Encounter) => eachEncounter.visitPayEligible
  );
  let totalpaymentPlans = 0;
  if (paymentPlans.length) {
    totalpaymentPlans = paymentPlans
      .map((v: any) => parseFloat(v.totalBalance))
      .reduce((a: number, b: number) => a + b);
  }

  const billingData = encounters.map(
    (each: Encounter): TableData => ({
      left: each.dischargeDate ? formatDate(new Date(each.dischargeDate)) : NA,
      middle: `${each.patient.firstName} ${each.patient.lastName}`,
      right: each.paymentPlan ? DASH : formatMoneyOrDash(each.balance),
      id: each.id
    })
  );

  if (billingData && billingData.length < 3) {
    billingData.push(
      ...paidEncounters.slice(0, 3 - billingData.length).map(
        (each: PaidEncounter): TableData => ({
          left: each.dischargeDate ? formatDate(new Date(each.dischargeDate)) : NA,
          middle: `${each.patient.firstName} ${each.patient.lastName}`,
          right: each.paymentPlan ? DASH : formatMoneyOrDash(each.balance),
          id: each.id
        })
      )
    );
  }

  const paymentHistoryData = payments.map(
    (each: Transaction): TableData => ({
      left: formatDate(new Date(each.date)),
      middle: each.description,
      right: formatMoneyOrDash(each.amount),
      id: each.receiptNumber
    })
  );

  const paymentPlanData = paymentPlans.map(
    (each: any): TableData => ({
      id: each.paymentPlanId,
      left: each.paymentDueDate ? formatDate(new Date(each.paymentDueDate)) : NA,
      middle: 'Payment Plan',
      right: formatMoneyOrDash(each.monthlyBalanceDue)
    })
  );

  const collectionsTableData = collections.map(
    (each: PaidEncounter): TableData => ({
      left: each.dischargeDate ? formatDate(new Date(each.dischargeDate)) : NA,
      middle: `${each.patient.firstName} ${each.patient.lastName}`,
      right: each.paymentPlan ? DASH : formatMoneyOrDash(each.balance),
      id: each.id
    })
  );

  const goToPaymentHistory = () => {
    analyticsService.logEvent(AnalyticsEvent.ViewAllPaymentHistoryClicked, data);
    history.push('/u/billing/payment-history');
  };

  const goToBillSummary = () => {
    analyticsService.logEvent(AnalyticsEvent.ViewAllBillsClicked, data);
    history.push('/u/billing/bills');
  };

  const goToPaymentDetail = (item: TableData) => {
    analyticsService.logEvent(AnalyticsEvent.IndividualPaymentClicked, data);
    history.push(`/u/billing/payment-detail/${item.id}`);
  };

  const goToCollectionsSummary = () => {
    analyticsService.logEvent(AnalyticsEvent.ViewAllCollectionsClicked, data);
    history.push('/u/billing/collections');
  };

  const goToVisitPayHome = () => {
    analyticsService.logEvent(AnalyticsEvent.ProceedWithPaymentClicked, data);
    setVisitPayURL(VISIT_PAY_HOME);
    setGoToVisitPay(true);
  };

  const goToVisitPayPaymentPlan = () => {
    analyticsService.logEvent(AnalyticsEvent.ProceedWithPaymentClicked, data);
    setVisitPayURL(VISIT_PAY_PAYMENT_PLAN);
    setGoToVisitPay(true);
  };

  const goToBillingDetail = (item: TableData) => {
    analyticsService.logEvent(AnalyticsEvent.IndividualBillingClicked, data);
    history.push(`/u/billing/bill-detail/${item.id}`, {
      eaid: item.id
    });
  };

  const generatePaymentPlanText = () => {
    if (!isVPeligible) {
      return 'You may be eligible for a Payment Plan';
    }
    if (checkPaymentPlansEligiblity && !checkPaymentPlansEligiblity.eligibleForPaymentPlans) {
      return 'You are not eligible for a Payment Plan';
    }
    return '';
  };

  const goToSpendingDetail = (currentPlan: string) => {
    if (currentPlan === 'Health Saving Account') {
      analyticsService.logEvent(AnalyticsEvent.HSAViewDetailsClicked, {
        currentUrl,
        referringUrl
      });
      history.push('/u/hsaDetails', {
        hsaData
      });
    } else {
      history.push('/u/manage-account/insurance');
    }
  };

  const generateSpentYearDate = (plan: Record<string, any>, currentPlan: string) => {
    let value;
    if (currentPlan === 'SelectHealth Med') {
      value = plan?.deductible - plan?.remainder?.deductible;
    } else {
      value = plan?.max_oop - plan?.remainder?.max_oop;
    }
    return formatMoney(value);
  };

  const generateDeductible = (plan: Record<string, any>, currentPlan: string) => {
    if (currentPlan === 'Health Saving Account') {
      return formatMoney(oc(hsaData).hsaDetails.availableBalance(0.0));
    }
    return formatMoney(currentPlan === 'SelectHealth Med' ? plan?.deductible : plan?.max_oop);
  };

  const renderApplyPaymentPlan = () => {
    if (checkPaymentPlansEligiblity && checkPaymentPlansEligiblity.eligibleForPaymentPlans) {
      return (
        <TouchableOpacity onPress={goToVisitPayPaymentPlan}>
          <MuiBox justifyContent="space-between" display="flex" padding="20px 20px 0px 20px">
            <Typography color={Color.secondary}>Apply for Payment Plan</Typography>
            <KeyboardArrowRight />
          </MuiBox>
        </TouchableOpacity>
      );
    }
    return null;
  };

  const { width } = Dimensions.get('window');
  const largeView = width > dfdDefaultTheme.breakpoints.values.md;

  return (
    <>
      {(guarantorLoading || collectionLoading) && !skip && renderSpinner()}
      <GreyView
        accessibilityLabel="Billing Summary View"
        isImpersonating={isImpersonating}
        pointerEvents={isImpersonating ? 'none' : 'auto'}
      >
        <CallToActionRow
          isPatientToolbar
          definitions={definitions}
          isBillingHome
          justifyContent="flex-end"
        />
        <MuiContainer maxWidth="lg">
          <CollapsibleList>
            <CollapsibleListItem
              largeView={largeView}
              leftIcon="Money"
              title="Bills"
              key={`Bills ${isImpersonating}`}
              expandOnFirstRender={isExpanded || false}
              isExpandable={!isImpersonating}
              subtitle={
                isImpersonating
                  ? 'Information unavailable'
                  : iff(
                      totalEncounters === 0,
                      '0 Unpaid',
                      `(${totalEncounters} Unpaid) $${formatMoney(amountDue)}`
                    )
              }
              subtitle1={
                isImpersonating
                  ? 'Information unavailable'
                  : iff(totalEncounters === 0, '0 Unpaid', `${totalEncounters} Unpaid Bills Due`)
              }
              subtitle2={isImpersonating ? '' : `Total Amount Due: $${formatMoney(amountDue)}`}
              analyticsEvent={AnalyticsEvent.BillsClicked}
              currentUrlForAnalytics={currentUrl}
              referringUrlForAnalytics={referringUrl}
            >
              <AmountSummaryTable
                largeView={largeView}
                arrows
                data={billingData}
                onItemPressed={goToBillingDetail}
                hideHeaders={!largeView}
                headers={['Visit Date', 'Patient', 'Amount']}
              />
              {billingData.length > 0 ? (
                <TouchableOpacity
                  onPress={goToBillSummary}
                  accessibilityLabel="Tap to view all unpaid bills"
                >
                  <MuiBox
                    justifyContent="space-between"
                    display="flex"
                    padding="20px 20px 0px 20px"
                  >
                    <MuiBox data-testid="view-all-unpaid-bills">
                      <Typography color={Color.secondary}>View All</Typography>
                    </MuiBox>
                    <MuiBox justifyContent="space-between" flexDirection="row">
                      <KeyboardArrowRight />
                    </MuiBox>
                  </MuiBox>
                </TouchableOpacity>
              ) : (
                <EmptyMuiBox>
                  <DefaultText>No Bills Are Currently Due</DefaultText>
                </EmptyMuiBox>
              )}
            </CollapsibleListItem>
            <Divider />

            <CollapsibleListItem
              largeView={largeView}
              leftIcon="PaymentHistory"
              title="Payment History"
              key={`Payment ${isImpersonating}`}
              isExpandable={!isImpersonating}
              subtitle={
                isImpersonating
                  ? 'Information unavailable'
                  : iff(
                      paymentHistoryData.length !== 0,
                      `Last Paid ${oc(paymentHistoryData)[0].right(0)} on ${oc(
                        paymentHistoryData
                      )[0].left('')}`,
                      '0 Payments'
                    )
              }
              analyticsEvent={AnalyticsEvent.PaymentHistoryClicked}
              currentUrlForAnalytics={currentUrl}
              referringUrlForAnalytics={referringUrl}
            >
              <AmountSummaryTable
                largeView={largeView}
                emptyCaption=""
                arrows
                data={paymentHistoryData}
                onItemPressed={goToPaymentDetail}
                hideHeaders={!largeView}
                headers={['Paid on', 'Patient', 'Amount']}
              />
              {paymentHistoryData.length === 0 ? (
                <EmptyMuiBox>
                  <DefaultText>No Payment History</DefaultText>
                </EmptyMuiBox>
              ) : (
                <TouchableOpacity
                  onPress={goToPaymentHistory}
                  accessibilityLabel="Tap to view all payment history"
                >
                  <MuiBox
                    justifyContent="space-between"
                    display="flex"
                    padding="20px 20px 0px 20px"
                  >
                    <MuiBox id="view-all-payment-history">
                      <Typography color={Color.secondary}>View All</Typography>
                    </MuiBox>
                    <MuiBox justifyContent="space-between" flexDirection="row">
                      <KeyboardArrowRight />
                    </MuiBox>
                  </MuiBox>
                </TouchableOpacity>
              )}
            </CollapsibleListItem>
            {!hsaLoading && (!isEmpty(hsaData) || myBenefits) ? (
              <>
                <Divider />
                <CollapsibleListItem
                  largeView={largeView}
                  leftIcon="Spending"
                  title="Spending Summary"
                  subtitle="HSA & Deductible progress"
                  isExpandable={!isImpersonating}
                  analyticsEvent={AnalyticsEvent.SpendingSummaryClicked}
                  currentUrlForAnalytics={currentUrl}
                  referringUrlForAnalytics={referringUrl}
                >
                  <TouchablePanelGroup
                    data={spendingSummaryData}
                    emptyCaption=""
                    keyExtractor={(itemBody: SpendSummaryBody) => `${itemBody.right}`}
                    onItemPressed={(itemBody: SpendSummaryBody) =>
                      goToSpendingDetail(itemBody.title)
                    }
                    renderItem={(itemBody: SpendSummaryBody) => {
                      return (
                        <MuiBox
                          data-testid={itemBody.testId}
                          aria-label="Tap for more info on spending summary"
                          justifyContent="space-between"
                          alignItems="center"
                          title="SelectHealth Facets Insurance"
                          display="flex"
                        >
                          <MuiBox flex="1">
                            <Svg set="assets" name={`${itemBody.id}`} size={IconSize.large} />
                          </MuiBox>
                          <MuiBox flex="8">
                            <Typography fontWeight={FontWeight.semibold} fontSize={FontSize.large}>
                              {itemBody.title === 'Health Saving Account'
                                ? itemBody.title
                                : selectHealthFacetsInsurance.insuranceName}
                            </Typography>
                            {itemBody.id === 'SelectHealth' ? (
                              <Typography
                                fontSize={FontSize.small}
                              >{`Spent year to date Individual: $${generateSpentYearDate(
                                myBenefits,
                                itemBody.title
                              )}`}</Typography>
                            ) : null}
                            <Typography fontSize={FontSize.small}>{`${
                              itemBody.left
                            }: $${generateDeductible(myBenefits, itemBody.title)}`}</Typography>
                          </MuiBox>
                          <MuiBox flex="4">
                            {itemBody.right && familyBenefits ? (
                              <>
                                <Typography
                                  fontWeight={FontWeight.semibold}
                                  fontSize={FontSize.large}
                                >
                                  &nbsp;
                                </Typography>
                                <Typography fontSize={FontSize.small}>
                                  {`Spent year to date Family: $${generateSpentYearDate(
                                    familyBenefits,
                                    itemBody.title
                                  )}`}
                                </Typography>
                                <Typography fontSize={FontSize.small}>
                                  {`${itemBody.right}: $${generateDeductible(
                                    familyBenefits,
                                    itemBody.title
                                  )}`}
                                </Typography>
                              </>
                            ) : null}
                          </MuiBox>
                          <MuiBox flex="inherit">
                            <Svg set="material" name="keyboard_arrow_right" />
                          </MuiBox>
                        </MuiBox>
                      );
                    }}
                  />
                </CollapsibleListItem>
              </>
            ) : null}
            {collectionsTableData.length === 0 ? null : (
              <>
                <Divider />
                <CollapsibleListItem
                  largeView={largeView}
                  leftIcon="Collections"
                  title="Collections"
                  subtitle="Check your bills in collections"
                  isExpandable={!isImpersonating}
                  analyticsEvent={AnalyticsEvent.CollectionsClicked}
                  currentUrlForAnalytics={currentUrl}
                  referringUrlForAnalytics={referringUrl}
                >
                  <AmountSummaryTable
                    largeView={largeView}
                    arrows
                    emptyCaption=""
                    data={collectionsTableData}
                    onItemPressed={goToBillingDetail}
                    headers={['Visit Date', 'Patient', 'Amount']}
                  />
                  {collectionsTableData.length < 3 ? null : (
                    <TouchableOpacity
                      onPress={goToCollectionsSummary}
                      accessibilityLabel="Tap to view all collections"
                    >
                      <MuiBox
                        justifyContent="space-between"
                        display="flex"
                        padding="20px 20px 0px 20px"
                      >
                        <MuiBox data-testid="view-all-unpaid-bills">
                          <Typography color={Color.secondary}>View All</Typography>
                        </MuiBox>
                        <MuiBox justifyContent="space-between" flexDirection="row">
                          <KeyboardArrowRight />
                        </MuiBox>
                      </MuiBox>
                    </TouchableOpacity>
                  )}
                </CollapsibleListItem>
              </>
            )}
            {!paymentPlansError ? (
              <>
                <Divider />
                <CollapsibleListItem
                  leftIcon="PaymentPlan"
                  title="Payment Plan"
                  isExpandable={!isImpersonating}
                  subtitle={
                    isImpersonating
                      ? 'Information unavailable'
                      : iff(
                          paymentPlans.length === 0,
                          'Check for eligibility',
                          `Total balance $${formatMoney(totalpaymentPlans)}`
                        )
                  }
                  analyticsEvent={AnalyticsEvent.PaymentPlanClicked}
                  currentUrlForAnalytics={currentUrl}
                  referringUrlForAnalytics={referringUrl}
                >
                  {paymentPlansLoading ? (
                    <CenteredSpinner />
                  ) : (
                    <AmountSummaryTable
                      arrows
                      largeView={largeView}
                      hideHeaders={!largeView}
                      onItemPressed={goToVisitPayHome}
                      emptyCaption={generatePaymentPlanText()}
                      data={paymentPlanData}
                      headers={['Due Date', 'Plan', 'Due Amount']}
                    />
                  )}
                  {renderApplyPaymentPlan()}
                </CollapsibleListItem>
              </>
            ) : null}
            <Divider />
            {Config.FA_APP === 'enabled' ? (
              <>
                <Divider />
                <CollapsibleListItem
                  largeView={largeView}
                  leftIcon="Bill"
                  title="Financial Assistance"
                  subtitle="Get help paying your bills"
                  isExpandable={!isImpersonating}
                  analyticsEvent={AnalyticsEvent.ApplyForAssistanceClicked}
                  currentUrlForAnalytics={currentUrl}
                  referringUrlForAnalytics={referringUrl}
                >
                  <FinancialAssistanceBody history={history} />
                </CollapsibleListItem>
              </>
            ) : null}
          </CollapsibleList>
          {goToVisitPay && visitPayURL && (
            <VisitPayWebView closePopup={setGoToVisitPay} uri={visitPayURL} />
          )}
        </MuiContainer>
      </GreyView>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isImpersonating: isImpersonatingSelector(state),
  shInsurance: selectHealthFacetsInsuranceSelector(state),
  glossary: getGlossaries(state),
  myBenefits: myPlanBenefitsSelector(state),
  familyBenefits: familyPlanBenefitsSelector(state),
  selectHealthFacetsInsurance: selectHealthFacetsInsuranceSelector(state),
  currentUrl: currentLocationPathNameSelector(state),
  referringUrl: previousLocationPathNameSelector(state)
});

const mapDispatchToProps = (dispatch: Function) => ({
  createSnack: (type: SnackType, snack?: Snack) => dispatch(snacksActions.createSnack(type, snack))
});

const component: ComponentType =
  Config.BILLING_EXPANSION_ENABLED === 'enabled'
    ? BillingHomeScreen
    : connect(mapStateToProps, mapDispatchToProps)(BillingHome as ComponentType);

export default component;
